import { Skeleton, Typography } from '@mui/material';
import { RiMore2Line, RiPencilLine } from '@remixicon/react';
import Money from '@shared/utils/Money';
import { useMemo } from 'react';

import LedgerAccountTypeChip from 'src/components/atoms/LedgerAccountTypeChip';
import { AgButton, AgSwitch } from 'src/components/ui';
import { AgDropdownItem, AgDropdownMenu } from 'src/components/ui/AgDropdownMenu';
import { LedgerAccountDetails } from 'src/modules/accounting/useLedgerAccounts';
import { GridColObj } from 'src/modules/grids';
import { formatCurrency } from 'src/utils/grid/cell/cellFormatters';

import { AccountWithBalance } from '../AccountsTab';

export const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  CashAssets: 'Cash Assets',
  CurrentAssets: 'Current Assets',
  LongTermAssets: 'Long-Term Assets',
  OtherAssets: 'Other Assets',
  CurrentLiabilities: 'Current Liabilities',
  LongTermLiabilities: 'Long-Term Liabilities',
  Equity: 'Equity',
  Income: 'Income',
  OtherIncome: 'Other Income',
  DirectCosts: 'Cost of Goods Sold',
  IndirectCosts: 'Operating Expenses',
  OtherCosts: 'Other Expenses',
};

interface UseAccountColumnsProps {
  onEdit: (account: Partial<LedgerAccountDetails>) => void;
  onDisable: (account: LedgerAccountDetails) => void;
  onDelete: (account: LedgerAccountDetails) => void;
  accounts: LedgerAccountDetails[];
  defaultAccountIds: Set<number>;
}

export default function useAccountColumns({
  onEdit,
  onDisable,
  onDelete,
  accounts,
  defaultAccountIds,
}: UseAccountColumnsProps): GridColObj {
  return useMemo(
    () => ({
      accountType: {
        headerName: 'Account type',
        field: 'accountType',
        type: 'string',
        flex: 1,
        minWidth: 180,
        getFilterSelectOptions: Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => ({ value, label })),
        renderCell: ({ row }) => {
          const account = row as LedgerAccountDetails;
          return <LedgerAccountTypeChip type={account.accountType} label={ACCOUNT_TYPE_LABELS[account.accountType]} />;
        },
      },
      isPhysical: {
        headerName: 'Allow Journal Entries',
        field: 'isPhysical',
        type: 'boolean',
        flex: 0.5,
        minWidth: 100,
      },
      balance: {
        headerName: 'Balance',
        field: 'balance',
        type: 'number',
        customFilterType: 'money',
        flex: 1,
        minWidth: 150,
        serverFilterValueTransform: {
          to: (value) => (value !== null ? Money.toCents(value as number) : null),
          from: (value) => (value !== null ? Money.toDollars(value as number) : null),
        },
        renderCell: (params) => {
          const account = params.row as AccountWithBalance;
          const rowNode = params.rowNode;
          const hasChildren = rowNode.type === 'group';
          // Check if this group node has visible children (is expanded)
          const isExpanded = hasChildren && rowNode.children && rowNode.children.length > 0;

          if (account.balance === 'loading') {
            return (
              <Skeleton>
                <Typography sx={{ fontSize: '14px' }}>$0,000.00</Typography>
              </Skeleton>
            );
          }

          // For parent rows: show only own balance if expanded, total if collapsed
          // For leaf rows: always show balance (which is just ownBalance)
          let displayBalance: number | null;
          if (hasChildren && isExpanded) {
            displayBalance = account.ownBalance === 'loading' ? null : account.ownBalance;
          } else {
            displayBalance = account.balance;
          }

          return (
            <Typography sx={{ fontSize: '14px' }}>
              {displayBalance === null ? '--' : formatCurrency({ value: displayBalance / 100 })}
            </Typography>
          );
        },
      },
      actions: {
        headerName: '',
        field: 'actions',
        disableExport: true,
        resizable: false,
        sortable: false,
        filterable: false,
        minWidth: 120,
        width: 120,
        renderCell: ({ row }) => {
          const account = row as LedgerAccountDetails;
          const isDefault = defaultAccountIds.has(account.id);
          const hasChildren = accounts.some((a) => a.parentId === account.id);
          const cantDisable = !account.disabled && isDefault;
          const cantDelete = isDefault || hasChildren;
          return (
            <div className="flex items-center gap-1">
              <AgSwitch isSelected={!account.disabled} isDisabled={cantDisable} onChange={() => onDisable(account)} />
              <AgButton
                variant="ghost"
                size="icon-sm"
                shape="circle"
                excludeFromTabOrder
                onPress={() => onEdit(account)}
              >
                <RiPencilLine size={20} />
              </AgButton>
              <AgDropdownMenu
                size="icon-sm"
                icon={<RiMore2Line size={20} />}
                variant="ghost"
                excludeFromTabOrder
                items={
                  <AgDropdownItem destructive isDisabled={cantDelete} onAction={() => onDelete(account)}>
                    Delete account
                  </AgDropdownItem>
                }
              />
            </div>
          );
        },
      },
    }),
    [onEdit, onDisable, onDelete, accounts, defaultAccountIds]
  );
}
